import { uniq } from "lodash";

import { useRef, useState } from "react";

import { css } from "@emotion/react";

import { Button } from "components/shared/library/Button";
import { DropdownFilter } from "components/shared/library/DropdownFilter";
import { HorizontalSlider } from "components/shared/library/HorizontalSlider";

import { FilterValue } from "apis/jobs";

import { useJobSearch } from "utils/jobsSearch";
import { isViewMobile } from "utils/mediaQueryStrings";

import { JobTypeFilter } from "./JobTypeFilter";

const style = {
  wrapper: css({
    width: "-webkit-fill-available",
    padding: "12px 16px 12px 16px",
    background: "#FFFFFF",
    gap: 16,
    minHeight: 32,
  }),
  slider: css({
    display: "flex",
    gap: 16,
  }),
  sliderWrapper: css({
    display: "flex",
    gap: 16,
    justifyContent: "space-between",
  }),
};

type DatePosted = NonNullable<FilterValue<"datePosted">>;
type Remote = NonNullable<FilterValue<"remote">>;
type Location = NonNullable<FilterValue<"location">>;

export const FilterMenuBar = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { upsertJobSearchFilter, jobSearch, clearSearchFilters } =
    useJobSearch();
  const [datePosted, setDatePosted] = useState<string[]>(
    [jobSearch.search?.filters?.datePosted || []].flat()
  );
  const [location, setLocation] = useState<string[]>(
    [jobSearch.search?.filters?.location || []].flat()
  );
  const [modality, setModality] = useState<string[]>(
    [jobSearch.search?.filters?.remote || []].flat()
  );
  const defaultLocations = [
    "New York, NY, USA",
    "San Francisco, CA, USA",
    "Toronto, ON, Canada",
  ];

  return (
    <div css={style.wrapper}>
      <HorizontalSlider
        size="small"
        externalRef={ref}
        showButtons={!isViewMobile()}
      >
        <div css={style.sliderWrapper}>
          <div css={style.slider}>
            <JobTypeFilter parentDivRef={ref} />
            <DropdownFilter
              primaryButtonLabel="Show results"
              onSubmit={(value) => {
                upsertJobSearchFilter("datePosted", value as DatePosted);
              }}
              setSelectedValues={setDatePosted}
              selectedValues={datePosted}
              label="Date posted"
              options={[
                { label: "Anytime", name: "anytime" },
                { label: "Past month", name: "pastMonth" },
                { label: "Past week", name: "pastWeek" },
                { label: "Past 24 hours", name: "past24Hours" },
              ]}
              defaultValue="anytime"
            />
            <DropdownFilter
              primaryButtonLabel="Show results"
              onSubmit={(value) => {
                upsertJobSearchFilter("location", value as Location);
              }}
              selectedValues={location}
              setSelectedValues={setLocation}
              options={uniq([
                ...[jobSearch.search?.filters?.location || []].flat(),
                ...defaultLocations,
              ])
                .sort()
                .map((loc) => ({ label: loc, name: loc }))}
              label="Location"
              title="Add location"
              searchEnabled
              allowMultiple
            />
            <DropdownFilter
              primaryButtonLabel="Show results"
              onSubmit={(value) => {
                upsertJobSearchFilter("remote", value as Remote);
              }}
              selectedValues={modality}
              setSelectedValues={setModality}
              label="Remote"
              options={[
                { label: "Remote", name: "remote" },
                { label: "Hybrid", name: "hybrid" },
                { label: "On-site", name: "on-site" },
              ]}
              allowMultiple
            />
          </div>
          <Button
            background="ghost"
            color="black"
            size="small"
            onClick={() => {
              setDatePosted(["anytime"]);
              setModality([]);
              setLocation([]);
              clearSearchFilters();
            }}
          >
            Clear all
          </Button>
        </div>
      </HorizontalSlider>
    </div>
  );
};
